<script setup lang="ts">
import { computed } from 'vue';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import PlaylistContainerSourceLine from '@/components/DataTable/PlaylistContainerSourceLine.vue';
import RecordLink from '@/components/RecordLink.vue';
import PlaylistContainerCounts from '@/components/DataTable/PlaylistContainerCounts.vue';
import CustomListStatusIcon from '@/components/DataTable/CustomListStatusIcon.vue';
import { nameWithCustomListStatus } from '@/components/DataTable/customListStatus';
import ScheduleCardSummaryLine from '@/components/schedules/ScheduleCardSummaryLine.vue';
import ScheduleCountsButton from '@/components/schedules/ScheduleCountsButton.vue';
import { modelIconForRow } from '@/composables/modelIcons';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';
import { readZoneDisplayString } from '@/utils/zoneDisplayString';

/**
 * Fixed card header for the two track containers, a bucket and a custom list.
 * Both cards say the same things in the same places: which zone the record
 * belongs to, what it is called, and what it draws from. Each count sits at the
 * right end of the line for the thing it counts.
 *
 *   <zone mark> <Location>-<Zone>                        5 ◎   (zones subscribing to this zone, custom lists only)
 *   <mark> <name> <status>                           1,204 ♫
 *   <mark> <source bucket> follows <vault mark> <vault>       (only when there is a source bucket)
 *   Schedules                                       [2|4|1]   (custom lists only)
 *   <rail> <other zone>                                       (only when another zone wrote the schedule)
 *   <rail> <days> <time> · <date window>
 *   <rail> <glyph> <type>
 *   <rail> <ordering>
 *   ...the same three or four lines again for each other schedule the search matched
 *
 * A bucket card has the first three lines only. A vault has no zone, so it has
 * no zone line, and a bucket's source line reads "follows <vault>"
 * ({@link PlaylistContainerSourceLine}, which the inspector heading draws too).
 *
 * The counts share a right edge ({@link PlaylistContainerCounts}, which the
 * inspector heading draws too). The numbers right-align in one column and the
 * icons stack in the column after it, so a two-digit count and a one-digit
 * count still line up. The schedule counts end at the same right edge. Clicking
 * the track count opens the inspector on its Tracks section, and clicking the
 * schedule counts opens it on Schedules.
 *
 * Every name that can be long takes up to two lines and then clips with an
 * ellipsis, each on its own. That holds for the zone, the record's name, the
 * source bucket and the vault, and for a name drawn as a link. The width is
 * the text's to decide, not a breakpoint's, so the card reads the same at
 * 300px and at full width with nothing hidden behind a count. The full name is
 * the tooltip of each line. During a quick search the zone line wraps in full
 * while it holds a match, so the highlight is never hidden behind the
 * ellipsis.
 *
 * The parent bucket is read through the registry's shared reader, which the
 * inspector heading uses too. They had a copy each and the copies disagreed:
 * for a row carrying a flat id and name, the inspector showed a link and the
 * card said "No source bucket".
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        entityId: string;
        /**
         * False while the row does not carry the list's schedules yet, so the schedule counts
         * are left out rather than drawn as a false "0 | 0".
         */
        showsScheduleCounts?: boolean;
        /**
         * Draw a checkbox on each of a custom list's schedule lines. Ticking one emits
         * `toggle-schedule` with the schedule's id. The list's own checkbox belongs to the
         * search card around this header, not to the header.
         */
        schedulesSelectable?: boolean;
        /** The schedule ids that are ticked, so their checkboxes stay showing. */
        selectedScheduleIds?: ReadonlySet<number>;
        /**
         * Make the record's own name a link to its page, with a side-peek button.
         * The drawer of a row's buckets or custom lists turns this on, because
         * there the card names a RELATED record. On the search page the card is
         * the record itself, and clicking the card already selects it.
         */
        linksName?: boolean;
        /**
         * The quick-search fields this row matched (SAU-591), passed in by the
         * search page. Each matched name is highlighted where it is drawn. The
         * source bucket can still clip, so a match there also gets the card's
         * extra line. The record pane's related cards pass nothing and highlight
         * nothing.
         */
        matchedFields?: readonly string[];
    }>(),
    {
        showsScheduleCounts: true,
        schedulesSelectable: false,
        selectedScheduleIds: () => new Set<number>(),
        linksName: false,
        matchedFields: () => [],
    },
);

const emit = defineEmits<{
    /** A schedule line's checkbox was clicked. The event carries the shift key. */
    (e: 'toggle-schedule', scheduleId: number, event: MouseEvent): void;
}>();

const titleText = computed(() => {
    const display = props.row.display_string ?? props.row.name;
    if (typeof display === 'string' && display.trim() !== '') {
        return display;
    }
    return `#${props.row.id}`;
});

const recordIcon = computed(() => modelIconForRow(props.entityId, props.row));

const isCustomList = computed(() => props.entityId === 'LocationZoneCustomPlaylist');

/**
 * The grid row of each part below the zone. A vault has no zone, and an empty first row would
 * still take a row gap above the name, so a bucket with no zone moves every part up one row. A
 * list keeps the first row, because its subscriber count sits there. The class names are
 * written out whole so Tailwind finds them.
 */
const hasZoneRow = computed(() => isCustomList.value || readZoneDisplayString(props.row.location_zone) !== null);
const rows = computed(() =>
    hasZoneRow.value
        ? { name: 'row-start-2', source: 'row-start-3', schedules: 'row-start-4', scheduleLines: 'row-start-5' }
        : { name: 'row-start-1', source: 'row-start-2', schedules: 'row-start-3', scheduleLines: 'row-start-4' }
);

/** The record's own id, for the name link. Null when the row carries none. */
const ownRecordId = computed<number | string | null>(() => {
    const id = props.row.id;
    if (typeof id === 'number' && Number.isFinite(id)) return id;
    if (typeof id === 'string' && id.trim() !== '') return id;
    return null;
});

const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);
const nameMatched = computed(() => props.matchedFields.includes('display_string') || props.matchedFields.includes('name'));

/** The whole name, for the tooltip, because it clips. A custom list's status follows its name. */
const headingTitle = computed(() =>
    isCustomList.value ? nameWithCustomListStatus(titleText.value, props.row.location_zone_custom_playlist_status_id) : titleText.value
);
</script>

<template>
    <!-- Three columns: the text, then the counts' numbers, then their icons. The
         numbers right-align, and the icons line up on the right edge. Rows:
         zone, name, source, then for a list the schedules label and its lines. -->
    <div class="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-1 gap-y-0.5" data-testid="playlist-container-card-header">
        <!-- The size is the caller's: the zone line sets none of its own, so this
             reads text-xs on a card and text-sm under the inspector heading.
             A bucket has no count on this line, so its zone line takes the whole row. -->
        <!-- Only this zone line gets the row's matches. The schedule lines below draw
             other zones' lines, which must not highlight this row's zone match. -->
        <PlaylistContainerZoneLine
            :row="row"
            :matched-fields="matchedFields"
            :max-lines="2"
            class="col-start-1 row-start-1 mr-1 min-w-0 text-xs"
            :class="isCustomList ? '' : 'col-span-3'"
        />
        <h3
            class="col-start-1 mr-1 flex min-w-0 items-center gap-1.5 text-sm font-semibold leading-tight"
            :class="rows.name"
            :title="headingTitle"
            data-testid="card-name-row"
        >
            <component
                :is="recordIcon"
                v-if="recordIcon"
                class="size-4 shrink-0"
                aria-hidden="true"
                data-testid="card-record-icon"
            />
            <!-- The name takes up to two lines and then clips. A link sits inside
                 the clamped box, so it clips with the plain name. -->
            <span class="min-w-0 line-clamp-2" data-testid="card-record-name"
                ><RecordLink
                    v-if="linksName && ownRecordId !== null"
                    :entity-id="entityId"
                    :record-id="ownRecordId"
                    :label="titleText"
                    inline
                    ><SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="nameMatched">{{
                        titleText
                    }}</SearchHighlight></RecordLink
                ><SearchHighlight v-else :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="nameMatched">{{
                    titleText
                }}</SearchHighlight></span>
            <!-- Right after the name, so a disabled list reads as disabled before anything else on the card. -->
            <CustomListStatusIcon v-if="isCustomList" :status-id="row.location_zone_custom_playlist_status_id" />
        </h3>
        <!-- The zone's subscriber count on row one, and the track count on row two. -->
        <PlaylistContainerCounts :row="row" :entity-id="entityId" :name-row="hasZoneRow ? 2 : 1" />
        <!-- What the record draws from. It has no count, so it starts at the left edge and takes the whole row. -->
        <PlaylistContainerSourceLine
            :row="row"
            :entity-id="entityId"
            :matched-fields="matchedFields"
            class="col-span-3 col-start-1 text-xs"
            :class="rows.source"
        />
        <template v-if="isCustomList">
            <!-- A small label at the left, with the schedule counts at the right. The
                 counts span both count columns and end at the same right edge as the
                 counts above. -->
            <p class="text-muted-foreground col-start-1 mt-1 text-xs" :class="rows.schedules" data-testid="card-schedules-label">Schedules</p>
            <ScheduleCountsButton
                v-if="showsScheduleCounts"
                :row="row"
                class="col-span-2 col-start-2 mt-1 justify-self-end"
                :class="rows.schedules"
            />
            <!-- The list's matching schedules, one line each, across the whole row. -->
            <ScheduleCardSummaryLine
                class="col-span-3 col-start-1 mt-0.5"
                :class="rows.scheduleLines"
                :row="row"
                :selectable="schedulesSelectable"
                :selected-schedule-ids="selectedScheduleIds"
                @toggle-schedule="(scheduleId, event) => emit('toggle-schedule', scheduleId, event)"
            />
        </template>
    </div>
</template>
